// Classes
import CCliente from '../cliente/CCliente';
import CUsuario from '../usuario/CUsuario';
import type CMonitoramentoTempoUso from './CMonitoramentoTempoUso';
import type CResultadoAgregacao from '../temas/base/CResultadoAgregacao';
import type CResponseConsultaPaginada from '../base/CResponseConsultaPaginada';

// Enums
import ECodificacao from '@/shared/enums/ECodificacao';
import ENivelUsuarioERP from '@/shared/enums/ENivelUsuarioERP';

// Stores
import type { TMenuSibebar } from '@/stores/TemaStore';

// Shared
import { obterConfigTema } from '@/shared/constants/ConstantesTemas';
import { cMENUS_DE_TODOS_TEMAS } from '@/shared/constants/ConstantesTemas';

// Modules
import cMENUS_TEMA_ADMINISTRATIVO from '@/modules/temas/tema-administrativo/MenusTemaAdministrativo';

export class CRegistroRecursosMaisUtilizados {
  tempoEmMs: number = 0;
  acessos: number = 0;
  usuario: string = '';
  nivel: ENivelUsuarioERP = ENivelUsuarioERP.Indefinido;
  cliente: string = '';
  identificador: string = '';

  constructor(pResponse: CResultadoAgregacao<CMonitoramentoTempoUso>) {
    this.acessos = pResponse.metricas?.count_sessaoId ?? this.acessos;
    this.tempoEmMs = pResponse.metricas?.sum_tempoEmMS ?? this.tempoEmMs;

    const lUsuario = pResponse.agregacao?.usuario ? new CUsuario(pResponse.agregacao?.usuario as any) : new CUsuario();
    const lCliente = pResponse.agregacao?.cliente ? new CCliente(pResponse.agregacao?.cliente as any) : new CCliente();

    // Adicionado fallback simples para evitar casos de "undefined - undefined" em instâncias malformadas
    this.usuario =
      `${lUsuario.codigo ?? ''} - ${lUsuario.nome ?? ''}`.replace(/^- | -$/g, '').trim() || 'Usuário Indefinido';
    this.cliente =
      `${lCliente.codigo ?? ''} - ${lCliente.descricao ?? ''}`.replace(/^- | -$/g, '').trim() || 'Cliente Indefinido';
    this.nivel = lUsuario.nivel;

    this.identificador = pResponse.agregacao?.identificador ?? this.identificador;
  }
}

export class CRegistroRecursosMaisUtilizadosPorCodificacao {
  codificacao: string = '';
  tempoEmMs: number = 0;

  constructor(pObj?: Partial<CRegistroRecursosMaisUtilizadosPorCodificacao>) {
    Object.assign(this, pObj);

    this.codificacao = obterConfigTema(this.codificacao as ECodificacao).titulo;
  }
}

export class CRegistroRecursosMaisUtilizadosPorTela {
  identificador: string = '';
  tempoEmMs: number = 0;

  constructor(pObj?: Partial<CRegistroRecursosMaisUtilizadosPorTela>) {
    Object.assign(this, pObj);
  }
}

export class CRegistroRecurosMaisUtilizadosPorCliente {
  cliente: string = '';
  temaMaisUsado: string = '';
  tempoTemaMs: number = 0;
  telaMaisUsada: string = '';
  tempoTelaMs: number = 0;

  constructor(pObj?: Partial<CRegistroRecurosMaisUtilizadosPorCliente>) {
    Object.assign(this, pObj);
  }
}

export default class CRelatorioRecursosMaisUtilizados {
  registros: CRegistroRecursosMaisUtilizados[] = [];
  registrosPorTela: CRegistroRecursosMaisUtilizadosPorTela[] = [];
  registrosPorCliente: CRegistroRecurosMaisUtilizadosPorCliente[] = [];
  registrosPorCodificacao: CRegistroRecursosMaisUtilizadosPorCodificacao[] = [];

  constructor(pResponse?: CResponseConsultaPaginada<CResultadoAgregacao<CMonitoramentoTempoUso>>) {
    if (pResponse) {
      this.processar(pResponse);
    }
  }

  processar(pResponse: CResponseConsultaPaginada<CResultadoAgregacao<CMonitoramentoTempoUso>>) {
    // Limpando os arrays no início do processamento para garantir idempotência e evitar o "Efeito Sanfona"
    this.registros = [];
    this.registrosPorTela = [];
    this.registrosPorCliente = [];
    this.registrosPorCodificacao = [];

    const menus = [...cMENUS_DE_TODOS_TEMAS, ...cMENUS_TEMA_ADMINISTRATIVO];

    const agregacaoPorTela = new Map<string, number>();
    const agregacaoPorCodificacao = new Map<ECodificacao, number>();
    const cacheRotas = new Map<string, ECodificacao | undefined>();

    const usoTelaPorCliente = new Map<string, Map<string, number>>();
    const usoTemaPorCliente = new Map<string, Map<ECodificacao, number>>();

    for (const registro of pResponse.registros) {
      const novoRegistro = new CRegistroRecursosMaisUtilizados(registro);
      this.registros.push(novoRegistro);

      const nameRoute = novoRegistro.identificador;
      const tempoMs = novoRegistro.tempoEmMs;
      const nomeCliente = novoRegistro.cliente;

      if (nameRoute) {
        // Agregação Global por Tela
        const tempoTotalTela = agregacaoPorTela.get(nameRoute) || 0;
        agregacaoPorTela.set(nameRoute, tempoTotalTela + tempoMs);

        // 1. Agregação de Tela (identificador) por Cliente
        if (!usoTelaPorCliente.has(nomeCliente)) {
          usoTelaPorCliente.set(nomeCliente, new Map<string, number>());
        }
        const mapTelasDoCliente = usoTelaPorCliente.get(nomeCliente)!;
        mapTelasDoCliente.set(nameRoute, (mapTelasDoCliente.get(nameRoute) || 0) + tempoMs);

        // Identificação da Codificação (Tema)
        let codificacao: ECodificacao | undefined;
        if (cacheRotas.has(nameRoute)) {
          codificacao = cacheRotas.get(nameRoute);
        } else {
          codificacao = this.encontrarCodificacaoPorRota(menus, nameRoute);
          cacheRotas.set(nameRoute, codificacao);
        }

        if (codificacao === undefined) {
          console.warn(`Atenção: A rota "${nameRoute}" não encontrou correspondência nos menus.`);
        }

        if (codificacao !== undefined && codificacao !== ECodificacao.Indefinido) {
          // Agregação global original
          const tempoAtual = agregacaoPorCodificacao.get(codificacao) || 0;
          agregacaoPorCodificacao.set(codificacao, tempoAtual + tempoMs);

          // 2. Agregação de Tema (codificacao) por Cliente
          if (!usoTemaPorCliente.has(nomeCliente)) {
            usoTemaPorCliente.set(nomeCliente, new Map<ECodificacao, number>());
          }
          const mapTemasDoCliente = usoTemaPorCliente.get(nomeCliente)!;
          mapTemasDoCliente.set(codificacao, (mapTemasDoCliente.get(codificacao) || 0) + tempoMs);
        }
      }
    }

    this.registrosPorTela = Array.from(
      agregacaoPorTela,
      ([identificador, tempoEmMs]) => new CRegistroRecursosMaisUtilizadosPorTela({ identificador, tempoEmMs }),
    );

    this.registrosPorCodificacao = Array.from(
      agregacaoPorCodificacao,
      ([codificacao, tempoEmMs]) => new CRegistroRecursosMaisUtilizadosPorCodificacao({ codificacao, tempoEmMs }),
    );

    this.gerarAnaliseDeCampeoesPorCliente(usoTelaPorCliente, usoTemaPorCliente);
  }

  public gerarAnaliseDeCampeoesPorCliente(
    usoTelaPorCliente: Map<string, Map<string, number>>,
    usoTemaPorCliente: Map<string, Map<ECodificacao, number>>,
  ) {
    // Pega todos os clientes únicos que tiveram algum uso
    const todosClientes = new Set([...usoTelaPorCliente.keys(), ...usoTemaPorCliente.keys()]);

    for (const cliente of todosClientes) {
      let telaMaisUsada = 'Nenhuma';
      let maxTempoTela = 0;

      // Encontra a tela com maior tempo para este cliente
      const telasDoCliente = usoTelaPorCliente.get(cliente);
      if (telasDoCliente) {
        for (const [tela, tempo] of telasDoCliente.entries()) {
          // Aplicando critério de desempate (ordem alfabética) para resultados determinísticos
          if (tempo > maxTempoTela || (tempo === maxTempoTela && tela.localeCompare(telaMaisUsada) < 0)) {
            maxTempoTela = tempo;
            telaMaisUsada = tela;
          }
        }
      }

      let temaMaisUsado: ECodificacao | undefined = undefined;
      let maxTempoTema = 0;

      // Encontra o tema com maior tempo para este cliente
      const temasDoCliente = usoTemaPorCliente.get(cliente);
      if (temasDoCliente) {
        for (const [tema, tempo] of temasDoCliente.entries()) {
          // Aplicando critério de desempate
          if (
            tempo > maxTempoTema ||
            (tempo === maxTempoTema &&
              (temaMaisUsado === undefined || String(tema).localeCompare(String(temaMaisUsado)) < 0))
          ) {
            maxTempoTema = tempo;
            temaMaisUsado = tema;
          }
        }
      }

      // Adiciona o resultado formatado no array de análise
      this.registrosPorCliente.push(
        new CRegistroRecurosMaisUtilizadosPorCliente({
          cliente: cliente,
          telaMaisUsada: telaMaisUsada,
          tempoTelaMs: maxTempoTela,
          temaMaisUsado: temaMaisUsado !== undefined ? obterConfigTema(temaMaisUsado).codificacao : 'Nenhum',
          tempoTemaMs: maxTempoTema,
        }),
      );
    }
  }

  /**
   * Busca recursivamente a codificação de um menu com base no nameRoute.
   * * @param menus Array de itens do menu (TMenuSibebar[])
   * @param targetNameRoute O nameRoute que estamos procurando
   * @param codificacaoPai Codificação herdada do menu pai na árvore
   * @returns A codificação correspondente ou undefined se não encontrar
   */
  public encontrarCodificacaoPorRota(
    menus: TMenuSibebar[],
    targetNameRoute: string,
    codificacaoPai?: ECodificacao,
  ): ECodificacao | undefined {
    for (const menu of menus) {
      // Herda a codificação do pai caso o menu atual não possua uma definida
      const codificacaoAtual = menu.codificacao ?? codificacaoPai;

      // 1. Verifica se o item atual é o que estamos procurando
      if (menu.nameRoute === targetNameRoute) {
        return codificacaoAtual;
      }

      // 2. Se o item tiver filhos, chama a função recursivamente para eles
      if (menu.children && menu.children.length > 0) {
        const codificacaoEncontrada = this.encontrarCodificacaoPorRota(
          menu.children,
          targetNameRoute,
          codificacaoAtual,
        );

        // Se encontrou nos filhos, retorna imediatamente parando o loop
        if (codificacaoEncontrada !== undefined) {
          return codificacaoEncontrada;
        }
      }
    }

    // 3. Se percorreu tudo e não encontrou, retorna undefined
    return undefined;
  }
}
